import React, { forwardRef } from 'react';
import styled from 'styled-components';

import { ThemeConfig } from '#/service/config';
import {
  ButtonShape,
  ButtonVariant,
  ButtonVariantConfig,
} from '#/service/constants';
import { Dictionary, pascal } from '#/service/utils';

import { focusShadow } from '../common';
import { Spinner, SpinnerThickness } from '../Spinner';

interface StyledProps {
  theme: ThemeConfig;
  $variant: ButtonVariant;
}

export interface BaseButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  $variant: ButtonVariant;
}

const buttonShapeRadius: Dictionary<ButtonShape, string> = {
  [ButtonShape.Pill]: '100rem',
  [ButtonShape.Squared]: '0',
  [ButtonShape.Rounded]: '0.44rem',
};

export const getBorderRadius = ({ theme, $variant }: StyledProps) => {
  const variantConfig = $variant?.toLowerCase() as ButtonVariantConfig;
  const shape = theme.button?.[variantConfig]?.shape ?? ButtonShape.Pill;

  if (!Object.values(ButtonVariant).includes($variant)) {
    console.warn('Invalid button variant:', $variant);
  }

  return `border-radius: ${buttonShapeRadius[shape]};`;
};

const getSharedStyle = (variant, state = '') => {
  return `
    background: var(--button${variant}BackgroundLinearGradient${state}) padding-box;
    z-index:0;

    &::before {
      content: '';
      position: absolute;
      top: -2px;
      left: -2px;
      width: 100%;
      height: 100%;
      border: inherit;
      border-radius: inherit;
      background: var(--button${variant}BorderLinearGradient${state}) border-box;
      mask: var(--button${variant}BorderLinearGradient) padding-box, var(--button${variant}BorderLinearGradient) border-box;
      -webkit-mask-composite: xor;
      mask-composite: exclude;
      z-index: -1;
    }
  `;
};

export const getVariantStyle = (props: StyledProps) => {
  const { $variant } = props;
  const variant = pascal($variant);

  if ($variant === ButtonVariant.Ghost) {
    return `
      color: var(--buttonPrimaryForeground);
      background: transparent;
      border: none;
    `;
  }

  return `
    color: var(--button${variant}Foreground);
    border: 2px solid transparent;  
    background-clip: padding-box;

    ${getSharedStyle(variant)}
  `;
};

const getDisabledStyle = (props: StyledProps) => {
  const { $variant } = props;
  const variant = pascal($variant);

  return `
    cursor: default;
    ${getSharedStyle(variant, 'Disabled')}
  `;
};

const getHoverStyle = (props: StyledProps) => {
  const { $variant } = props;
  const variant = pascal($variant);

  return getSharedStyle(variant, 'Hover');
};

export const BaseButton = styled.button<BaseButtonProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  outline: none;
  font-size: inherit;
  line-height: 1rem;
  word-break: break-word;
  padding: 0.5rem 2rem;
  cursor: pointer;

  &:hover:enabled {
    ${getHoverStyle}
  }

  &.focus-visible {
    ${focusShadow}
  }

  ${getVariantStyle}
  ${getBorderRadius}
  

  ${({ disabled }) => disabled && getDisabledStyle}

  &[data-full-width='true'] {
    width: 100%;
    text-align: center;
    display: block;
  }
`;

BaseButton.displayName = 'BaseButton';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual type of the button @default ButtonVariant.Primary */
  variant?: ButtonVariant;

  /** Adds a spinner on top of the button to signal awaiting response @default false */
  isLoading?: boolean;

  /** Custom text that will appear alongside the loading spinner. By default, only spinner will be shown @default '' */
  loadingText?: string;

  /** Prop that will make the button to use full width available and assure that text is centered @default false  */
  isFullWidth?: boolean;
}

/** Basic button component */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = ButtonVariant.Primary,
      isLoading = false,
      children = null,
      loadingText = '',
      type = 'button',
      isFullWidth = false,
      disabled,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled ?? isLoading;

    return (
      <BaseButton
        ref={ref}
        type={type}
        {...rest}
        $variant={variant}
        data-full-width={isFullWidth}
        disabled={isDisabled}
      >
        {isLoading ? (
          <>
            <StyledSpinner
              animationDuration="0.7s"
              thickness={SpinnerThickness.Bold}
            />
            {loadingText}
          </>
        ) : (
          children
        )}
      </BaseButton>
    );
  }
);

Button.displayName = 'Button';

const StyledSpinner = styled(Spinner)`
  margin-right: 0.2rem;
`;

export default Button;
